import {
  ContactType,
  PatientListDto,
  NumberSource,
  PatientState,
  PatientStatus,
  QueryFactory,
} from '@lmt/shared-types';
import {
  convertPropertyAccessorToFunction,
  StyledAutocomplete,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/StyledAutocomplete';
import { StyledAutocompleteProps } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/types';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { PatientContacts } from '@lmt/unicorn-shared/app/features/patients/types';
import { useSubtenantsDict } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { emptyArray } from '@lmt/unicorn-shared/app/helpers/common';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { localizeNumberWithSource } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { ReactComponent as ImgOpenLink } from '@lmt/unicorn-shared/app/icons/open-link.svg';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HighlightedText } from '../ui-kit/inputs/search/HighlightedText';
import optionListStyles from '../ui-kit/inputs/search/OptionList.module.scss';
import styles from './PatientSearch.module.scss';
import { TagsEditor } from '../ui-kit/tags/TagsEditor';
import { AutocompleteRenderOptionState, useEventCallback } from '@mui/material';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import {
  PatientSearchActions,
  patientSearchSelector,
} from '@lmt/unicorn-shared/app/features/patientSearchSlice';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

function useRecentlySearchedPatients() {
  const patientIds = useUniSelector(patientSearchSelector);
  return QueryFactory.PatientQuery.useGetPatientsQuery(
    { patientIds: patientIds },
    {
      enabled: !!patientIds?.length,
    },
  );
}

export interface PatientCase {
  id: number;
  number: string;
  startDate: Date;
  endDate: Date | null;
}

export interface IPatientWithCase extends PatientListDto {
  lastCase?: PatientCase;
}

export interface SearchInputPatientData extends PatientContacts {
  id: number;
  doctorId?: number | null;
  subtenantId: number;
  firstName: string;
  lastName: string;
  dateOfBirth: Date;
  priorityContactType: ContactType;
  number: string;
  numberSource: NumberSource;
  lastCase?: PatientCase;
}

type Props = Pick<
  StyledAutocompleteProps<PatientListDto>,
  'getOptionLabel' | 'renderInput'
> & {
  className?: string;
  optionContainerClassName?: string;
  isOnlyActive: boolean;
  selectedPatient?: SearchInputPatientData | null;
  onSelectedPatientChanged: (patient: SearchInputPatientData | null) => void;
  patientsLimit?: number;
  errorText?: string;
  onOpenExtendedSearchPage?: (searchText: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  optionListWidth?: number;
  variant?: 'normal' | 'formInput';
  selectPatientIfOnlyOneFound?: boolean;
  /**
   Show these patients as result when component is loaded
   */
  predefinedPatients?: IPatientWithCase[];

  /*
   * if true resets the input to an empty state after firing onSelectedPatientChanged/onOpenExtendedSearchPage
   */
  resetAfterSelect?: boolean;

  testId?: string;
};

export const PatientSearch: FC<Props> = (props) => {
  const { isOnlyActive, errorText, testId, getOptionLabel } = {
    ...props,
    getOptionLabel: props.getOptionLabel
      ? convertPropertyAccessorToFunction(props.getOptionLabel)
      : useCallback(
          (x: string | PatientListDto) =>
            !x
              ? ''
              : typeof x === 'string'
                ? x
                : x.lastName + ' ' + x.firstName,
          [],
        ),
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useUniDispatch();
  const i18n = useTranslation();
  const claims = useClaims();
  const i18next = useScopedTranslation('patient_search');
  const recentlySearchedPatients = useRecentlySearchedPatients();
  const [searchText, setSearchText] = useState('');
  const patientsQuery = QueryFactory.PatientQuery.useGetPatientsQuery(
    {
      searchQuery: searchText,
      states: claims.permissions.patientDeleteAndRestore
        ? [PatientState.Active, PatientState.Deleted, PatientState.Anonymous]
        : undefined,
      statuses: isOnlyActive ? [PatientStatus.Active] : undefined,
      pagination_Limit: props.patientsLimit ?? 10,
    },
    {
      enabled: !!searchText && searchText.length >= 1,
    },
  );
  const tabs = useTabs();
  const subtenants = useSubtenantsDict();
  const options =
    (!searchText
      ? (recentlySearchedPatients?.data?.data ?? emptyArray())
      : patientsQuery.data?.data) ?? emptyArray();

  // this is used to close the popup and reset
  const [key, setKey] = useState(1);
  const afterSelectCallback = useCallback(() => {
    if (props.resetAfterSelect) {
      setKey((key) => ++key);
      setSearchText('');
    }
  }, [props.resetAfterSelect]);

  const [showPredefinedAsResult, setShowPredefinedAsResult] =
    useState<boolean>(false);

  useEffect(
    function showPredefinedPatientsAsResult() {
      if (props.predefinedPatients?.length) {
        setShowPredefinedAsResult(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    },
    [props.predefinedPatients],
  );

  if (props.disabled) {
    return (
      <Input
        disabled={true}
        value={getOptionLabel(props.selectedPatient as any)}
        endAdornment={
          props.selectedPatient ? (
            <span className={styles.inputOpenPatientLinkBadge}>
              <ImgOpenLink
                className={styles.icon}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  tabs.openTab(
                    pages.patientProfile.link({
                      patientId: props.selectedPatient!.id,
                    }),
                  );
                }}
              />
            </span>
          ) : null
        }
      />
    );
  }
  return (
    <StyledAutocomplete
      key={key}
      variant={props.variant}
      className={props.className}
      disabled={props.disabled}
      renderInput={TagsEditorWrapper as any}
      options={showPredefinedAsResult ? props.predefinedPatients! : options}
      openOnFocus={showPredefinedAsResult ? true : undefined}
      inputRef={inputRef}
      errorText={errorText}
      freeSolo={true}
      isSearch={true}
      required={false}
      testId={testId}
      value={props.selectedPatient as PatientListDto}
      placeholder={i18n.t('uikit.search.placeholder')}
      getOptionLabel={getOptionLabel as any}
      popupHeader={
        !searchText &&
        !!recentlySearchedPatients?.data?.data?.length &&
        !showPredefinedAsResult ? (
          <div
            className={clsx(
              optionListStyles.recentlySearchedContainer,
              optionListStyles.recentSearchText,
            )}
          >
            {i18n.t('uikit.search.recent_searches')}
          </div>
        ) : null
      }
      renderOption={useCallback(
        (
          props: any,
          patient: IPatientWithCase,
          state: AutocompleteRenderOptionState,
        ) => (
          <div
            {...props}
            className={clsx(props.className, styles.muiNoBorder)}
            key={patient.id}
          >
            <HighlightedText
              textClassName={styles.text}
              text={`${patient.lastName} ${patient.firstName}`}
              highlight={state.inputValue}
            />
            <HighlightedText
              textClassName={styles.text}
              text={localFormat(patient.dateOfBirth, 'P')}
              highlight={state.inputValue}
            />
            {patient.lastCase && (
              <span className={styles.lastCase}>
                {i18next.t('last_case', {
                  number: patient.lastCase.number,
                  startDate: localFormat(patient.lastCase.startDate, 'P'),
                })}
              </span>
            )}
            <span className={styles.openPatientLink}>
              <span className={styles.number}>
                {localizeNumberWithSource(patient.number, patient.numberSource)}
              </span>
              <span className={styles.code}>
                {subtenants[patient.subtenantId]?.code}
              </span>
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dispatch(
                    PatientSearchActions.addSearchedPatientId(patient?.id),
                  );
                  tabs.openTab(
                    pages.patientProfile.link({ patientId: patient!.id }),
                  );
                  afterSelectCallback();
                }}
              >
                <ImgOpenLink className={styles.icon} />
              </span>
            </span>
          </div>
        ),
        [subtenants],
      )}
      // loading={patientsQuery.isLoading}
      onInputChange={useEventCallback(
        (e: React.SyntheticEvent, value: string) => {
          // if predefined patients were displayed they shouldn't be displayed after user's changes
          setShowPredefinedAsResult(false);
          setSearchText(value);
        },
      )}
      filterOptions={useCallback((x: IPatientWithCase[]) => {
        return x;
      }, [])}
      onChange={useEventCallback(
        (_: React.SyntheticEvent, value: string | IPatientWithCase | null) => {
          setShowPredefinedAsResult(false);
          if (typeof value === 'string') {
            if (props.selectPatientIfOnlyOneFound && options.length === 1) {
              const patient = options[0];
              dispatch(PatientSearchActions.addSearchedPatientId(patient.id));
              afterSelectCallback();
              props.onSelectedPatientChanged(patient);
            } else {
              props.onOpenExtendedSearchPage?.(value);
              afterSelectCallback();
            }
          } else {
            if (value) {
              dispatch(PatientSearchActions.addSearchedPatientId(value.id));
              afterSelectCallback();
            }
            props.onSelectedPatientChanged(value);
          }
        },
      )}
    />
  );
};

const TagsEditorWrapper: StyledAutocompleteProps<PatientListDto>['renderInput'] =
  React.forwardRef((inputProps, ref) => {
    const onChangeRef = useRef(inputProps.onChange);
    onChangeRef.current = inputProps.onChange;
    const onClickRef = useRef(inputProps.onClick);
    onClickRef.current = inputProps.onClick;
    const onBlurRef = useRef(inputProps.onBlur);
    onBlurRef.current = inputProps.onBlur;
    const onSelectRef = useRef(inputProps.onSelect);
    onSelectRef.current = inputProps.onSelect;
    const onKeyDownRef = useRef(inputProps.onKeyDown);
    onKeyDownRef.current = inputProps.onKeyDown;
    const onFocusRef = useRef(inputProps.onFocus);
    onFocusRef.current = inputProps.onFocus;
    const onMouseDownRef = useRef(inputProps.onMouseDown);
    onMouseDownRef.current = inputProps.onMouseDown;
    return (
      <TagsEditor
        error={!!inputProps.errorText}
        helperText={inputProps.errorText}
        {...(inputProps as any)}
        ref={ref as any}
        isSearch={true}
        isMultiline={false}
        className={clsx(
          styles.tagsEditor,
          !!inputProps.errorText ? styles.error : undefined,
        )}
        value={inputProps.value?.toString() ?? ''}
        createNonExistingTags={false}
        onChange={(e, newValue) => {
          onChangeRef.current?.(e as any);
        }}
        onClick={(e) => {
          onClickRef.current?.(e as any);
        }}
        onFocus={(e) => {
          onFocusRef.current?.(e as any);
        }}
        onBlur={(e) => {
          onBlurRef.current?.(e as any);
        }}
        onSelect={(e) => {
          onSelectRef.current?.(e as any);
        }}
        onKeyDown={(e) => {
          onKeyDownRef.current?.(e as any);
        }}
        onMouseDown={(e) => {
          onMouseDownRef.current?.(e as any);
        }}
      />
    );
  });
